import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import Modal from "./Modal";
import Select from "./Select";
import { useData } from "../context/DataContext";
import {
  formatKES,
  formatMonth,
  currentMonth,
  monthOptions,
  paidForMonth,
  paidForMonthByType,
  utilityChargeForMonth,
  addMonths,
} from "../utils/format";

// Dialog to record a payment. Supports rent (with multi-month distribution)
// and utility payments (single-month).
// allowedTypes: optional array to restrict which charge types are shown.
// e.g. ["water", "electricity"] hides rent. Default: show all applicable.
export default function RecordPaymentDialog({ open, onClose, tenant, defaultMonth, allowedTypes }) {
  const { payments, properties, utilityCharges, recordPayment, recordUtilityPayment } = useData();

  const [chargeType, setChargeType] = useState("rent");
  const [amount, setAmount] = useState("");
  const [startMonth, setStartMonth] = useState(defaultMonth || currentMonth());
  const [note, setNote] = useState("");

  const property = properties.find((p) => p.id === tenant?.propertyId);
  const hasWater = property?.waterEnabled;
  const hasElec = property?.electricityEnabled;

  // Compute charge amount and already-paid based on selected type.
  const chargeAmount = useMemo(() => {
    if (!tenant) return 0;
    if (chargeType === "rent") return Number(tenant.rent) || 0;
    return utilityChargeForMonth(utilityCharges, tenant.id, startMonth, chargeType);
  }, [tenant, chargeType, startMonth, utilityCharges]);

  const alreadyPaid = useMemo(() => {
    if (!tenant) return 0;
    if (chargeType === "rent") return paidForMonth(payments, tenant.id, startMonth);
    return paidForMonthByType(payments, tenant.id, startMonth, chargeType);
  }, [tenant, chargeType, startMonth, payments]);

  const balance = Math.max(0, chargeAmount - alreadyPaid);

  const typeOptions = [
    (!allowedTypes || allowedTypes.includes("rent")) && { v: "rent", l: "Rent" },
    hasWater && (!allowedTypes || allowedTypes.includes("water")) && { v: "water", l: "Water" },
    hasElec && (!allowedTypes || allowedTypes.includes("electricity")) && { v: "electricity", l: "Electricity" },
  ].filter(Boolean);

  useEffect(() => {
    if (!open || !tenant) return;
    const initialType = typeOptions[0]?.v || "rent";
    setChargeType(initialType);
    setStartMonth(defaultMonth || currentMonth());
    setNote("");
  }, [open, tenant, defaultMonth]);

  // When chargeType, startMonth, or underlying data changes, update the pre-filled amount.
  useEffect(() => {
    if (!open || !tenant) return;
    setAmount(balance > 0 ? String(balance) : chargeAmount > 0 ? String(chargeAmount) : "");
  }, [chargeType, startMonth, balance, chargeAmount, open, tenant]);

  // Preview: for rent, shows multi-month distribution + utility settlement.
  // For utility-only payments, shows single month.
  const preview = useMemo(() => {
    if (!tenant) return [];
    const total = Number(amount) || 0;
    if (total <= 0) return [];

    if (chargeType !== "rent") {
      // Utility: single month, no spill.
      return [{ month: startMonth, amount: total, type: chargeType, coversFull: total + alreadyPaid >= chargeAmount }];
    }

    // Rent allocation rules (match the backend in
    // app/routes/payments.py record_payment):
    //   1. Partial fill is allowed ONLY for the starting month.
    //   2. Every subsequent month must be FULLY covered, or the
    //      rent walk stops and the remainder spills to utilities
    //      (step 2) then discard (step 3).
    const out = [];
    let remaining = total;
    let m = startMonth;
    const rent = Number(tenant.rent) || 0;
    const max = rent > 0 ? 24 : 1;

    for (let i = 0; i < max && remaining > 0.0001; i++) {
      const already = paidForMonth(payments, tenant.id, m);
      const need = Math.max(0, rent - already);
      if (need <= 0) {
        // Already settled — advance without consuming the start-month
        // partial privilege.
        m = addMonths(m, 1);
        continue;
      }
      if (remaining + 0.0001 < need) {
        // Can't fully cover this month. Partial is only OK on the
        // user's selected starting month; for any later month we
        // stop and let the remainder spill to utilities.
        if (m === startMonth) {
          out.push({ month: m, amount: remaining, type: "rent", coversFull: false });
          remaining = 0;
        }
        break;
      }
      out.push({ month: m, amount: need, type: "rent", coversFull: true });
      remaining -= need;
      m = addMonths(m, 1);
    }

    // Step 2: settle utility charges for start month with remainder
    if (remaining > 0.0001) {
      for (const utilType of ["water", "electricity"]) {
        if (remaining <= 0.0001) break;
        const charge = utilityChargeForMonth(utilityCharges, tenant.id, startMonth, utilType);
        if (charge <= 0) continue;
        const utilPaid = paidForMonthByType(payments, tenant.id, startMonth, utilType);
        const utilNeed = Math.max(0, charge - utilPaid);
        if (utilNeed <= 0) continue;
        const alloc = Math.min(remaining, utilNeed);
        out.push({ month: startMonth, amount: alloc, type: utilType, coversFull: alloc + utilPaid >= charge });
        remaining -= alloc;
      }
    }

    // Step 3: show discarded amount if any
    if (remaining > 0.01) {
      out.push({ amount: remaining, type: "discarded" });
    }

    return out;
  }, [amount, startMonth, chargeType, chargeAmount, alreadyPaid, tenant, payments, utilityCharges]);

  const [submitting, setSubmitting] = useState(false);

  if (!tenant) return null;

  const submit = async (e) => {
    e.preventDefault();
    const total = Number(amount) || 0;
    if (total <= 0) return;
    setSubmitting(true);
    try {
      const trimmedNote = note.trim();
      if (chargeType === "rent") {
        await recordPayment({ tenantId: tenant.id, amount: total, startMonth, note: trimmedNote });
      } else {
        await recordUtilityPayment({
          tenantId: tenant.id,
          amount: total,
          month: startMonth,
          chargeType,
          note: trimmedNote,
        });
      }
      onClose();
    } catch {
      // error handled by api layer
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = chargeType === "rent" ? "Monthly rent" : chargeType === "water" ? "Water bill" : "Electricity bill";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record payment"
      description={`${tenant.name} · House ${tenant.houseNumber}`}
    >
      <form onSubmit={submit} className="space-y-4">
        {/* Charge type selector */}
        {typeOptions.length >= 1 && (
          <div>
            <label className="label-base">Payment for</label>
            <div className="flex flex-wrap gap-2">
              {typeOptions.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setChargeType(o.v)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                      chargeType === o.v
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label-base">Amount paid (KES)</label>
            <input
              type="number"
              className="input-base"
              value={amount}
              min="1"
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label-base">{chargeType === "rent" ? "Starting month" : "Month"}</label>
            <Select
              value={startMonth}
              onChange={setStartMonth}
              options={monthOptions()}
            />
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-xs">
          <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
            <span>{typeLabel}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {chargeAmount > 0 ? formatKES(chargeAmount) : "No bill entered"}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between text-gray-600 dark:text-gray-400">
            <span>Already paid for {formatMonth(startMonth)}</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatKES(alreadyPaid)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-gray-600 dark:text-gray-400">
            <span>Outstanding for {formatMonth(startMonth)}</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatKES(balance)}</span>
          </div>
        </div>

        {chargeType !== "rent" && chargeAmount === 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
            No {chargeType} bill has been entered for {formatMonth(startMonth)} yet.
            Enter the bill amount on the Utilities page first.
          </div>
        )}

        {preview.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Distribution
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
              {preview.map((p, i) => {
                if (p.type === "discarded") {
                  return (
                    <li key="discarded" className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-400 dark:text-gray-500">Unallocated (ignored)</span>
                      <span className="font-medium text-gray-400 dark:text-gray-500">{formatKES(p.amount)}</span>
                    </li>
                  );
                }
                const label = p.type === "rent"
                  ? formatMonth(p.month)
                  : `${p.type === "water" ? "Water" : "Electricity"} (${formatMonth(p.month)})`;
                return (
                  <li key={`${p.type}-${p.month}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-gray-900 dark:text-white">{label}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{formatKES(p.amount)}</span>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                          p.coversFull
                            ? "bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-950 dark:text-green-400 dark:ring-green-500/30"
                            : "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950 dark:text-amber-400 dark:ring-amber-500/30"
                        }`}
                      >
                        {p.coversFull ? "full" : "partial"}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div>
          <label className="label-base">Note (optional)</label>
          <textarea
            className="input-base"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. M-Pesa ref QAB1234XYZ, or any reference you want to remember"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Record payment
          </button>
        </div>
      </form>
    </Modal>
  );
}
